import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { BadgeCheck, ShieldCheck, Stethoscope } from "lucide-react";
import aboutMainImage from "../assets/family_care_about.png";
import aboutSideImage from "../assets/illustrations/about_workflows.png";
import heroOverlayImage from "../assets/illustrations/about_team.png";
import TopSectionDivider from "../components/TopSectionDivider";
import BottomSectionDivider from "../components/BottomSectionDivider";
// import SectionDivider from "../components/SectionDivider";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  accent: "#F4A896",
  background: "#F7FAF9",
  textMain: "#2E2E2E",
  textSub: "#4B5563",
  line: "#E6E8EC",
};

const AnimatedCounter = ({ to, duration = 1200 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;

    let startTime = null;
    let frameId = null;

    const animate = (time) => {
      if (!startTime) startTime = time;
      const progress = Math.min((time - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * to));

      if (progress < 1) frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isInView, to, duration]);

  return <span ref={ref}>{count}</span>;
};

const trustHighlights = [
  "Verified JANAK partners with safety-first care workflows",
  "Real-time care updates that keep families reassured",
  "Daily monitoring, regular health checks, and trusted assistance",
];

const AboutHero = () => {
  return (
    <>
    <section
      className="relative overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 12% 20%, rgba(58,175,169,0.12), transparent 34%), radial-gradient(circle at 88% 80%, rgba(123,200,164,0.2), transparent 36%), #F7FAF9",
      }}
    >
{/* <SectionDivider position="top" color="#F7FAF9" height={40} /> */}
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pt-8 pb-16 sm:pt-10 sm:pb-20 sm:px-6 lg:pt-12 lg:grid-cols-2 ">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div
            className="mb-5 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em]"
            style={{
              borderColor: `${COLORS.primary}66`,
              backgroundColor: `${COLORS.primary}12`,
              color: COLORS.primary,
            }}
          >
            About JANAK
          </div>

          <h1
            className="text-3xl font-extrabold leading-tight sm:text-4xl md:text-5xl"
            style={{ color: COLORS.textMain }}
          >
            Joint Assurance Network for Aging Kin
            <span style={{ color: COLORS.primary }}>
              {" "}
              for care beyond distance.
            </span>
          </h1>

          <p
            className="mt-6 max-w-xl text-base leading-relaxed sm:text-lg"
            style={{ color: COLORS.textSub }}
          >
            JANAK helps families care for aging parents with dignity and
            safety through daily monitoring, routine checkups, and
            trusted local assistance in one place.
          </p>

          <p
            className="mt-4 max-w-xl text-sm leading-relaxed sm:text-base"
            style={{ color: COLORS.textSub }}
          >
            It began with a simple realization: many of us live far away from
            our parents while they continue to live in the homes where we grew
            up. JANAK keeps a reliable care network close when you cannot be
            there physically.
          </p>

          <div className="mt-8 space-y-3">
            {trustHighlights.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <BadgeCheck
                  size={18}
                  style={{ color: COLORS.primary }}
                  className="mt-0.5"
                />
                <p
                  style={{ color: COLORS.textSub }}
                  className="text-sm sm:text-base"
                >
                  {item}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              className="rounded-full px-7 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(58,175,169,0.35)] transition hover:-translate-y-0.5"
              style={{ backgroundColor: COLORS.primary }}
            >
              Start Care Consultation
            </Link>
            <Link
              to="/services"
              className="rounded-full border px-7 py-3 text-sm font-semibold transition hover:-translate-y-0.5"
              style={{
                borderColor: COLORS.line,
                color: COLORS.textMain,
                backgroundColor: "#FFFFFF",
              }}
            >
              Explore Services
            </Link>
          </div>


        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex justify-center lg:justify-end items-center px-6 py-6"
        >
          {/* Decorative ambient color blur spot behind the collage */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-[#3AAFA9]/10 rounded-full blur-[80px] pointer-events-none" />

          {/* Main Card Wrapper */}
          <div className="relative w-full max-w-[400px] sm:max-w-[440px] aspect-square">
            
            {/* MAIN IMAGE */}
            <img
              src={aboutMainImage}
              alt="Happy family care"
              className="w-full h-full rounded-[2.5rem] object-cover border border-[#E6E8EC]/60 shadow-[0_24px_50px_rgba(58,175,169,0.14)]"
            />

            {/* Floating Top Right Video Card */}
            <motion.div
              animate={{
                y: [0, -8, 0]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -top-6 -right-6 w-[150px] sm:w-[190px] rounded-2xl border border-white/60 bg-white/75 backdrop-blur-md p-2 sm:p-2.5 shadow-[0_16px_36px_rgba(46,46,46,0.12)] z-20 hover:scale-105 transition-transform"
              style={{ borderColor: "rgba(255, 255, 255, 0.6)" }}
            >
              <img
                src={heroOverlayImage}
                alt="Trusted Care Team"
                className="h-16 sm:h-22 w-full rounded-xl object-cover bg-white"
              />
              <div className="mt-2 flex items-center gap-1.5 px-0.5">
                <Stethoscope size={12} style={{ color: COLORS.accent }} />
                <p
                  className="text-[9px] sm:text-[10px] font-bold"
                  style={{ color: COLORS.textMain }}
                >
                  Trusted Care Team
                </p>
              </div>
            </motion.div>

            {/* Floating Bottom Left Video Card */}
            <motion.div
              animate={{
                y: [0, 8, 0]
              }}
              transition={{
                duration: 6.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -bottom-6 -left-6 w-[150px] sm:w-[190px] rounded-2xl border border-white/60 bg-white/75 backdrop-blur-md p-2 sm:p-2.5 shadow-[0_16px_36px_rgba(46,46,46,0.12)] z-20 hover:scale-105 transition-transform"
              style={{ borderColor: "rgba(255, 255, 255, 0.6)" }}
            >
              <img
                src={aboutSideImage}
                alt="Verified Workflows"
                className="h-16 sm:h-22 w-full rounded-xl object-cover bg-white"
              />
              <div className="mt-2 flex items-center gap-1.5 px-0.5">
                <ShieldCheck size={12} style={{ color: COLORS.primary }} />
                <p
                  className="text-[9px] sm:text-[10px] font-bold"
                  style={{ color: COLORS.textMain }}
                >
                  Verified Workflows
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
        {/* <BottomSectionDivider color="#F7FAF9" height={40} /> */}
        

    </section>

    </>
  );
};

export default AboutHero;
